import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../../../firebase-config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { notification, Button, Input, Modal, Form, Space, Card, Typography, Upload } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UpOutlined, DownOutlined, UploadOutlined } from '@ant-design/icons';
import { storage } from '../../../../firebase-config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../../../provider/Auth.provider';

const { Title, Text } = Typography;

const TrainerEditor = () => {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [form] = Form.useForm();
  const { user } = useAuth();

  const fetchTrainers = useCallback(async () => {
    try {
      if (!user) {
        setTrainers([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const trainersCollection = collection(db, 'trainers');
      const q = query(trainersCollection, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const firebaseTrainers = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTrainers(firebaseTrainers);
      
    } catch (error) {
      let errorMessage = 'Failed to load trainers data.';
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your Firebase rules.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Firebase is temporarily unavailable. Please try again.';
      } else if (error.message.includes('orderBy')) {
        errorMessage = 'Error with sorting. Please check trainer order data.';
      }
      
      notification.error({
        message: 'Error',
        description: errorMessage,
        placement: 'topRight',
      });
      
      setTrainers([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchTrainers();
  }, [fetchTrainers]);

  const handleAdd = () => {
    setEditingTrainer(null);
    form.resetFields();
    form.setFieldsValue({
      order: trainers.length + 1
    });
    setModalVisible(true);
  };

  const handleEdit = (trainer) => {
    setEditingTrainer(trainer);
    form.setFieldsValue({
      name: trainer.name,
      position: trainer.position,
      alt: trainer.alt,
      order: trainer.order
    });
    setModalVisible(true);
  };

  const handleDelete = async (trainer) => {
    try {
      await deleteDoc(doc(db, 'trainers', trainer.id));
      notification.success({
        message: 'Success',
        description: 'Trainer deleted successfully.',
        placement: 'topRight',
      });
      fetchTrainers();
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to delete trainer.',
        placement: 'topRight',
      });
    }
  };

  const uploadImage = async (file) => {
    try {
      setUploadingImage(true);
      const storageRef = ref(storage, `trainers/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageUpload = async (info) => {
    if (info.file.status === 'uploading') {
      setUploadingImage(true);
      return;
    }
    if (info.file.status === 'done') {
      try {
        setUploadingImage(false);
        form.setFieldsValue({ imgLink: info.file });
        notification.success({
          message: 'Success',
          description: 'Image selected successfully. Upload will happen when you save the trainer.',
          placement: 'topRight',
        });
      } catch (error) {
        notification.error({
          message: 'Error',
          description: 'Failed to process image.',
          placement: 'topRight',
        });
      }
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (!user) {
        notification.error({
          message: 'Authentication Error',
          description: 'Please log in to manage trainers.',
          placement: 'topRight',
        });
        return;
      }

      if (!values.name || !values.position || !values.alt || !values.order) {
        notification.error({
          message: 'Error',
          description: 'Please fill in all required fields.',
          placement: 'topRight',
        });
        return;
      }
      
      if (!values.name.trim() || !values.position.trim() || !values.alt.trim()) {
        notification.error({
          message: 'Error',
          description: 'Please fill in all required fields with valid content.',
          placement: 'topRight',
        });
        return;
      }
      
      let imgLink = values.imgLink || editingTrainer?.imgLink;
      
      if (imgLink && typeof imgLink === 'object' && imgLink.fileList) {
        try {
          const latestFile = imgLink.fileList[imgLink.fileList.length - 1];
          
          if (!latestFile || !latestFile.originFileObj) {
            throw new Error('No valid file found in fileList');
          }
          
          const downloadURL = await uploadImage(latestFile.originFileObj);
          imgLink = downloadURL;
        } catch (error) {
          notification.error({
            message: 'Error',
            description: 'Failed to upload image.',
            placement: 'topRight',
          });
          return;
        }
      }
      
      if (!imgLink) {
        notification.error({
          message: 'Error',
          description: 'Please upload an image for the trainer.',
          placement: 'topRight',
        });
        return;
      }
      
      const order = parseInt(values.order);
      if (isNaN(order) || order < 1) {
        notification.error({
          message: 'Error',
          description: 'Please enter a valid order number (1 or higher).',
          placement: 'topRight',
        });
        return;
      }
      
      const trainerData = {
        name: String(values.name).trim(),
        position: String(values.position).trim(),
        imgLink: String(imgLink),
        alt: String(values.alt).trim(),
        order: Number(order),
        updatedAt: new Date().toISOString()
      };
      
      if (!trainerData.name || !trainerData.position || !trainerData.imgLink || !trainerData.alt) {
        notification.error({
          message: 'Error',
          description: 'All fields must contain valid data.',
          placement: 'topRight',
        });
        return;
      }
      
      if (editingTrainer) {
        await updateDoc(doc(db, 'trainers', editingTrainer.id), trainerData);
        notification.success({
          message: 'Success',
          description: 'Trainer updated successfully.',
          placement: 'topRight',
        });
      } else {
        const newOrder = trainers.length > 0 ? Math.max(...trainers.map(t => t.order)) + 1 : 1;
        const newTrainer = {
          ...trainerData,
          order: Number(newOrder),
          createdAt: new Date().toISOString()
        };
        
        const trainersCollection = collection(db, 'trainers');
        await addDoc(trainersCollection, newTrainer);
        notification.success({
          message: 'Success',
          description: 'Trainer added successfully.',
          placement: 'topRight',
        });
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchTrainers();
    } catch (error) {
      let errorMessage = 'Failed to save trainer.';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your Firebase rules.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Firebase is temporarily unavailable. Please try again.';
      } else if (error.code === 'not-found') {
        errorMessage = 'Collection not found. Please check your Firebase setup.';
      } else if (error.code === 'invalid-argument') {
        errorMessage = 'Invalid data format. Please check your input.';
      }
      
      notification.error({
        message: 'Error',
        description: errorMessage,
        placement: 'topRight',
      });
    }
  };

  const handleReorder = async (fromIndex, toIndex) => {
    try {
      const newTrainers = [...trainers];
      const [movedItem] = newTrainers.splice(fromIndex, 1);
      newTrainers.splice(toIndex, 0, movedItem);

      const updatePromises = newTrainers.map((trainer, index) => 
        updateDoc(doc(db, 'trainers', trainer.id), { order: index + 1 })
      );

      await Promise.all(updatePromises);
      notification.success({
        message: 'Success',
        description: 'Trainer order updated successfully.',
        placement: 'topRight',
      });
      fetchTrainers();
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to reorder trainers.',
        placement: 'topRight',
      });
    }
  };

  const uploadProps = {
    name: 'file',
    beforeUpload: () => false,
    onChange: handleImageUpload,
    showUploadList: false,
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      {!user ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Title level={3}>Authentication Required</Title>
          <Text>Please log in to manage trainers.</Text>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <Title level={2}>Manage Trainers</Title>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleAdd}
              size="large"
            >
              Add New Trainer
            </Button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Text>Loading trainers...</Text>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {trainers.map((trainer, index) => (
                <Card 
                  key={trainer.id} 
                  size="small"
                  style={{ 
                    border: '1px solid #d9d9d9',
                    backgroundColor: 'white'
                  }}
                  actions={[
                    <Button 
                      icon={<UpOutlined />} 
                      onClick={() => {
                        if (index > 0) handleReorder(index, index - 1);
                      }}
                      disabled={index === 0}
                    >
                      Move Up
                    </Button>,
                    <Button 
                      icon={<DownOutlined />} 
                      onClick={() => {
                        if (index < trainers.length - 1) handleReorder(index, index + 1);
                      }}
                      disabled={index === trainers.length - 1}
                    >
                      Move Down
                    </Button>,
                    <Button 
                      icon={<EditOutlined />} 
                      onClick={() => handleEdit(trainer)}
                    >
                      Edit
                    </Button>,
                    <Button 
                      danger 
                      icon={<DeleteOutlined />} 
                      onClick={() => handleDelete(trainer)}
                    >
                      Delete
                    </Button>
                  ]}
                >
                  <div style={{ display: 'flex', gap: 16 }}>
                    <img 
                      src={trainer.imgLink} 
                      alt={trainer.alt}
                      style={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <Title level={4} style={{ margin: 0 }}>
                          {trainer.name}
                        </Title>
                      </div>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
                        {trainer.position} • Order: {trainer.order}
                      </Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Alt: {trainer.alt}
                      </Text>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <Modal
        title={editingTrainer ? 'Edit Trainer' : 'Add New Trainer'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            name: '',
            position: '',
            alt: '',
            order: 1
          }}
        >
          <Form.Item
            name="name"
            label="Trainer Name"
            rules={[{ required: true, message: 'Please enter the trainer name!' }]}
          >
            <Input 
              placeholder="Enter trainer name..."
              style={{ fontSize: '16px' }}
            />
          </Form.Item>

          <Form.Item
            name="position"
            label="Position/Specialization"
            rules={[{ required: true, message: 'Please enter the position!' }]}
          >
            <Input 
              placeholder="e.g., Digital Marketing Expert, UI/UX Expert..."
              style={{ fontSize: '16px' }}
            />
          </Form.Item>

          <Form.Item
            name="imgLink"
            label="Trainer Image"
            rules={[
              {
                validator: (_, value) => {
                  if (!editingTrainer && !value) {
                    return Promise.reject(new Error('Please upload an image!'));
                  }
                  if (value && typeof value === 'object' && value.fileList && value.fileList.length > 0) {
                    return Promise.resolve();
                  }
                  if (value && typeof value === 'string' && value.startsWith('http')) {
                    return Promise.resolve();
                  }
                  if (!editingTrainer) {
                    return Promise.reject(new Error('Please upload an image!'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Upload {...uploadProps}>
              <Button 
                icon={<UploadOutlined />} 
                loading={uploadingImage}
              >
                {editingTrainer ? 'Change Image' : 'Upload Image'}
              </Button>
            </Upload>
            {editingTrainer && (
              <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                Leave empty to keep the current image
              </div>
            )}
          </Form.Item>

          <Form.Item
            name="alt"
            label="Image Alt Text"
            rules={[{ required: true, message: 'Please enter the alt text!' }]}
          >
            <Input 
              placeholder="e.g., Trainer Name - Position"
              style={{ fontSize: '16px' }}
            />
          </Form.Item>

          <Form.Item
            name="order"
            label="Display Order"
            rules={[{ required: true, message: 'Please enter the display order!' }]}
          >
            <Input 
              type="number" 
              min={1}
              placeholder="Enter display order (1, 2, 3...)"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingTrainer ? 'Update Trainer' : 'Add Trainer'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TrainerEditor; 